import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "@/firebase";
import { newId } from "./insightsModel";

// Images attached to an insight live in Storage under the project:
//   projects/{projectUid}/insights/{insightUid}/{id}_{filename}
// Colleagues can read/write there (storage.rules catch-all); project guests
// can read (sub = "insights") but not upload, matching their read-only UI.

export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

export function isImageFile(file) {
  return !!file && /^image\//.test(String(file.type || ""));
}

export function insightImagePath(projectUid, insightUid, name) {
  const safe = String(name || "image").replace(/[^\w.-]+/g, "_");
  return `projects/${projectUid}/insights/${insightUid}/${newId()}_${safe}`;
}

// Natural size of an image file, or null when it cannot be decoded.
export async function readImageSize(file) {
  try {
    if (typeof createImageBitmap === "function") {
      const bmp = await createImageBitmap(file);
      const size = { width: bmp.width, height: bmp.height };
      if (bmp.close) bmp.close();
      return size;
    }
  } catch (err) {
    /* fall through to the <img> route */
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

// Uploads one image and resolves to the row stored on the insight.
export async function uploadInsightImage({
  projectUid,
  insightUid,
  file,
  onProgress,
}) {
  if (!projectUid || !insightUid) throw new Error("Missing project or insight");
  if (!isImageFile(file)) throw new Error(`${file.name} is geen afbeelding`);
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(`${file.name} is groter dan 15 MB`);
  }
  const path = insightImagePath(projectUid, insightUid, file.name);
  const ref = storageRef(storage, path);
  const [size] = await Promise.all([
    readImageSize(file),
    new Promise((resolve, reject) => {
      const task = uploadBytesResumable(ref, file, { contentType: file.type });
      task.on(
        "state_changed",
        (snap) => {
          if (onProgress && snap.totalBytes) {
            onProgress(snap.bytesTransferred / snap.totalBytes);
          }
        },
        reject,
        resolve
      );
    }),
  ]);
  const url = await getDownloadURL(ref);
  const row = { url, path, name: file.name, contentType: file.type };
  if (size) {
    row.width = size.width;
    row.height = size.height;
  }
  return row;
}

// Best effort: a missing object is not an error worth surfacing.
export async function deleteInsightImage(path) {
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (err) {
    if (!err || err.code !== "storage/object-not-found") {
      console.warn("deleteInsightImage failed:", path, err);
    }
  }
}

// Paths present in `before` but no longer in `after` (matched on path).
export function imagePathsRemoved(before, after) {
  const keep = new Set(
    (after || []).map((img) => img && img.path).filter(Boolean)
  );
  return (before || [])
    .map((img) => img && img.path)
    .filter((path) => path && !keep.has(path));
}
